import {Box, Grid} from '@mui/material';
import {Trash} from 'iconsax-react';
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import UserInvitationField from '../UserInvitationField';
import {AppTable} from 'components/table';
import {UserListingResponseModel} from 'models/tenant.model';
import {ColumnDef} from '@tanstack/react-table';
import {useInviteMultipleUserToScopeByEmail} from 'hooks';
import {UserInviteScopeLevelEnum} from 'models';
import {AppIconButton} from 'components/buttons';
import {AppConfirmationDialog, AppConfirmationDialogRefType} from 'components/dialogs';

export type MemberShipBaseSettingHandleComponent = {
    handleInviteUserEmails: (values: string[]) => void;
    setAlertOpen: (open: boolean) => void;
};
interface MembershipBaseProps {
    searchValue: string;
    setSearchValue: (value: string) => void;
    selectedIds: string[];
    removeUserHandler: () => void;
    scopeId: string;
    scopeLevel: UserInviteScopeLevelEnum;
    handleRefreshData: () => void;
    columns: ColumnDef<UserListingResponseModel>[];
    data: UserListingResponseModel[];
}

export const MembershipBaseSetting = forwardRef<MemberShipBaseSettingHandleComponent, MembershipBaseProps>(
    (
        {
            columns,
            data,
            handleRefreshData,
            removeUserHandler,
            searchValue,
            scopeId,
            scopeLevel,
            selectedIds,
            setSearchValue,
        },
        ref,
    ) => {
        const memberShipBaseconfirmationDialiogRef = useRef<AppConfirmationDialogRefType>(null);
        const {
            inviteMultipleUserToScopeByEmail,
            inviteMultipleUserToScopeByEmailLoading,
            inviteMultipleUserToScopeByEmailResponse,
        } = useInviteMultipleUserToScopeByEmail();
        const handleInviteUserEmails = (values: string[]) => {
            if (values.length > 0) {
                inviteMultipleUserToScopeByEmail({
                    variables: {
                        request: {
                            scopeId: scopeId,
                            scopeLevel: scopeLevel,
                            inviteeEmails: values,
                        },
                    },
                });
            }
        };

        useImperativeHandle(ref, () => ({
            handleInviteUserEmails,
            setAlertOpen,
        }));

        const setAlertOpen = (open: boolean) => {
            if (memberShipBaseconfirmationDialiogRef) {
                open
                    ? memberShipBaseconfirmationDialiogRef.current?.handleOpen()
                    : memberShipBaseconfirmationDialiogRef.current?.handleClose();
            }
        };

        useEffect(() => {
            if (!inviteMultipleUserToScopeByEmailLoading && inviteMultipleUserToScopeByEmailResponse) {
                handleRefreshData();
            }
        }, [inviteMultipleUserToScopeByEmailLoading, inviteMultipleUserToScopeByEmailResponse]);
        return (
            <Box height={'calc(100vh - 180px)'} overflow={'auto'}>
                <Grid container spacing={1}>
                    <Grid item xs={12} display="flex" justifyContent={'flex-end'} alignItems={'center'}>
                        {selectedIds.length > 0 && (
                            <AppIconButton
                                sx={{minHeight: '35px', mr: 1, mt: 0.5, border: '1px solid'}}
                                color="error"
                                onClick={e => {
                                    if (memberShipBaseconfirmationDialiogRef.current) {
                                        memberShipBaseconfirmationDialiogRef.current.handleOpen();
                                    }
                                }}
                            >
                                <Trash size={'16px'} variant="Bold" color="red" />
                            </AppIconButton>
                        )}
                        <UserInvitationField
                            searchValue={searchValue}
                            loading={inviteMultipleUserToScopeByEmailLoading}
                            setSearchValue={value => setSearchValue(value)}
                            inviteSubmitHandler={handleInviteUserEmails}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AppTable<UserListingResponseModel> columns={columns} data={data} />
                    </Grid>

                    <AppConfirmationDialog
                        renderElement={(_open, handleOpen) => <></>}
                        ref={memberShipBaseconfirmationDialiogRef}
                        icon={<Trash variant="Bold" />}
                        onConfirmClick={(_open, _handleOpen, handleClose) => {
                            if (memberShipBaseconfirmationDialiogRef.current) {
                                removeUserHandler();
                                handleClose();
                            }
                        }}
                        title="Remove Users"
                        description={`Are you sure you want to remove ${selectedIds.length > 1 ? 'these users' : 'this user'}  from the ${scopeLevel.toString().toLowerCase()}?`}
                    />
                </Grid>
            </Box>
        );
    },
);

export default MembershipBaseSetting;
